import { el, inject, create, awaitRedraw } from "@dhx/ts-common/dom";
import { isEmptyObj } from "@dhx/ts-common/core";
import { Slider, SliderEvents } from "@dhx/ts-slider";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { Label } from "./helper/label";
import {
	ISliderFormConfig,
	ItemEvent,
	ISliderFormEventHandlersMap,
	ISliderForm,
	ISliderProps,
} from "../types";
import { baseProps } from "../helper";

export class SliderForm extends Label implements ISliderForm {
	public config: ISliderFormConfig;
	public slider: Slider;
	public events: IEventSystem<ItemEvent, ISliderFormEventHandlersMap> = new EventSystem();

	private _propsItem = ["label", "labelWidth", "labelPosition", "hiddenLabel", "helpMessage"];
	private _propsSlider = [
		"min",
		"max",
		"step",
		"mode",
		"range",
		"inverse",
		"tooltip",
		"tick",
		"tickTemplate",
		"majorTick",
	];
	private _props = [...baseProps, ...this._propsItem, ...this._propsSlider];

	constructor(container, config: ISliderFormConfig) {
		super(null, config);

		this._initView(config);
		this._initHandlers();

		const render = () => this._drawSlider();
		this.mount(container, create({ render }));
	}
	destructor() {
		this.events && this.events.clear();
		this.slider && this.slider.destructor();

		this.events = this._uid = this._propsSlider = this._propsItem = this._props = null;
		super._destructor();
		this.unmount();
	}
	setProperties(propertyConfig: ISliderProps): void {
		if (
			!propertyConfig ||
			isEmptyObj(propertyConfig) ||
			!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
		)
			return;

		for (const key in propertyConfig) {
			if (this._props.includes(key)) {
				this.config[key] = propertyConfig[key];
			}
		}
		this._initView(this.config);
		this._initHandlers();

		this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.slider.paint();
		this.paint();
	}
	getProperties(): ISliderProps {
		const obj: ISliderProps = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	show(): void {
		const { value, hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [value as number[]])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [value as number[]]);
	}
	hide(init?: boolean): void {
		const { value, hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [value as number[], init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [value as number[], init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.slider.disable();
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this.slider.enable();
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	clear(): void {
		const { value, min } = this.config;
		if (value[0] === min) return;
		this.slider.setValue(min);
	}
	getValue(): number[] {
		return this.slider.getValue();
	}
	setValue(value: number | number[]): void {
		if (typeof value === "undefined" || value === this.config.value) return;
		this.slider.setValue(value);
	}
	getWidget(): Slider {
		return this.slider;
	}
	focus(extra?: boolean): void {
		awaitRedraw().then(() => {
			this.slider.focus(extra);
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			this.slider.blur();
		});
	}
	protected _initView(config: ISliderFormConfig): void {
		if (isEmptyObj(config)) {
			throw new Error("Check the configuration is correct");
		}
		if (this.slider) {
			this.slider.destructor();
		}
		const sliderConfig: ISliderFormConfig = {
			type: config.type,
			id: config.id,
			name: config.name,
			mode: "horizontal",
			min: 0,
			max: 100,
			step: 1,
			range: false,
			inverse: false,
			tooltip: true,
			disabled: false,
			hidden: false,
			label: "",
			labelWidth: "",
			labelPosition: "top",
			hiddenLabel: false,
			helpMessage: "",
			width: "content",
			height: "content",
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				sliderConfig[key] = config[key];
			}
		}
		this.config = { type: this.config.type, ...sliderConfig };
		this.slider = new Slider(null, sliderConfig);

		this.config.disabled && this.slider.disable();
		this.config.value = this.slider.getValue();

		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}
	}
	protected _initHandlers() {
		this.slider.events.on(SliderEvents.change, () => {
			if (!this._uid) return;
			const value = (this.config.value = this.slider.getValue());
			this.events.fire(ItemEvent.change, [value]);
			this.paint();
		});
		this.slider.events.on(SliderEvents.focus, () => {
			this.events.fire(ItemEvent.focus, [this.getValue()]);
		});
		this.slider.events.on(SliderEvents.blur, () => {
			this.events.fire(ItemEvent.blur, [this.getValue()]);
		});
		this.slider.events.on(SliderEvents.keydown, (event: KeyboardEvent) => {
			this.events.fire(ItemEvent.keydown, [event]);
		});
	}
	protected _getRootView() {
		this.slider.paint();
		return this.slider.getRootView();
	}
	protected _drawSlider() {
		const { name, id } = this.config;
		return el(
			"div.dhx_form-group",
			{
				"data-dhx-id": name || id,
			},
			[inject(this._getRootView())]
		);
	}
}
